import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type WorkItem = Schemas["WorkItemDto"];
export type WorkDetails = Schemas["WorkDetailsDto"];
export type Me = Schemas["MeDto"];
export type ProjectSummary = Schemas["ProjectSummaryDto"];
export type KnowledgeItem = Schemas["KnowledgeItemDto"];
export type KnowledgeLink = Schemas["KnowledgeLinkDto"];
export type KnowledgeSearchHit = Schemas["KnowledgeSearchHitDto"];
export type KnowledgeVersion = Schemas["KnowledgeVersionDto"];
export type KnowledgeActivity = Schemas["KnowledgeActivityDto"];
export type RemoteKnowledge = Schemas["RemoteKnowledgeDto"];
export type Observation = Schemas["ObservationDto"];
export type LiveSession = Schemas["LiveSessionDto"];
export type SnapshotClaim = Schemas["SnapshotClaim"];
export type HubEvent = Schemas["EventDto"];
export type Credential = Schemas["CredentialDto"];
export type Member = Schemas["MemberDto"];
export type KnowledgeDraft = Schemas["KnowledgeDraftDto"];

export const CSRF_HEADER = "X-ProjectBrain-Csrf";

/** Erreur de l'API du hub (corps ErrorDto). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

// Session par cookie (BFF) : le navigateur ne manipule jamais de jeton ; l'en-tête anti-CSRF accompagne chaque requête.
const csrf: Middleware = {
  onRequest({ request }) {
    request.headers.set(CSRF_HEADER, "1");
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: "/", credentials: "same-origin" });
api.use(csrf);

let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

/** Déballe une réponse openapi-fetch : données, ou ApiError (401 → retour à la connexion). */
export async function unwrap<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await call;
  if (response.ok) return data as T;
  if (response.status === 401) onUnauthorized();
  const body = (error ?? {}) as { code?: string; message?: string };
  throw new ApiError(response.status, body.code ?? String(response.status), body.message ?? response.statusText);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}
